/**
 * Canonical operation / task / authority / evidence / AI / sync / recovery state grammar.
 * Semantic tokens only — no page-local color choices.
 */

import {
  PRODUCT_MUTATION_RESULT_ELEMENTS_V0,
  PRODUCT_P0_STATE_BOUNDARY_INVARIANTS_V0,
} from '@shared/product-experience-canon-and-claim-boundary';
import type { OperationalStatusToneV0 } from '@shared/surface-runtime/operational-status-grammar';

export const OPERATIONAL_SHELL_AND_COMPONENT_GRAMMAR_PROGRAM_ID_V0 =
  'OPERATIONAL-SHELL-AND-COMPONENT-GRAMMAR-V0' as const;

export const CANONICAL_OPERATION_STATES_V0 = [
  'DRAFT',
  'PROPOSED',
  'READY',
  'ADMITTED',
  'RUNNING',
  'WAITING',
  'NEEDS_ATTENTION',
  'BLOCKED',
  'DENIED',
  'FAILED',
  'COMPENSATING',
  'COMPLETED',
  'CANCELLED',
] as const;

export type CanonicalOperationStateV0 = (typeof CANONICAL_OPERATION_STATES_V0)[number];

export type StateNamespaceV0 =
  | 'operation'
  | 'task'
  | 'authority'
  | 'evidence'
  | 'AI'
  | 'sync'
  | 'recovery';

export const STATE_NAMESPACES_V0: readonly StateNamespaceV0[] = [
  'operation',
  'task',
  'authority',
  'evidence',
  'AI',
  'sync',
  'recovery',
] as const;

/** Required truth separations — consume predecessor P0 invariants. */
export const STATE_TRUTH_DISTINCTIONS_V0 = [
  {
    id: 'SCAN_NE_MUTATION',
    left: 'scan_success',
    right: 'business_mutation_completed',
    invariant: 'SCAN_NE_MUTATION',
  },
  {
    id: 'EVIDENCE_SUBMITTED_NE_ACCEPTED',
    left: 'evidence_submitted',
    right: 'evidence_accepted',
    invariant: 'EVIDENCE_UPLOADED_NE_ACCEPTED',
  },
  {
    id: 'APPROVAL_NE_EXECUTION',
    left: 'approval_granted',
    right: 'execution_completed',
    invariant: 'APPROVAL_NE_EXECUTION_COMPLETE',
  },
  {
    id: 'AI_PROPOSED_NE_ADMITTED',
    left: 'ai_proposed',
    right: 'system_admitted',
    invariant: 'AI_PROPOSED_NE_ADMITTED',
  },
  {
    id: 'EXECUTION_NE_RECONCILED',
    left: 'execution_completed',
    right: 'reconciled',
    invariant: 'EXECUTOR_SUCCESS_NE_RECONCILIATION',
  },
  {
    id: 'ACCEPTED_NE_EXECUTED',
    left: 'evidence_accepted',
    right: 'execution_completed',
    invariant: 'ACCEPTED_NE_EXECUTED',
  },
] as const;

export const OPERATION_STATE_TOKEN_MAP_V0: Record<
  CanonicalOperationStateV0,
  { tone: OperationalStatusToneV0; cssVar: string; labelKo: string }
> = {
  DRAFT: { tone: 'neutral', cssVar: '--badge-info-bg', labelKo: '초안' },
  PROPOSED: { tone: 'info', cssVar: '--badge-info-bg', labelKo: '제안됨' },
  READY: { tone: 'neutral', cssVar: '--badge-info-bg', labelKo: '준비됨' },
  ADMITTED: { tone: 'info', cssVar: '--badge-info-bg', labelKo: '수락됨' },
  RUNNING: { tone: 'info', cssVar: '--badge-info-bg', labelKo: '실행 중' },
  WAITING: { tone: 'warning', cssVar: '--badge-warning-bg', labelKo: '대기' },
  NEEDS_ATTENTION: { tone: 'warning', cssVar: '--badge-warning-bg', labelKo: '주의 필요' },
  BLOCKED: { tone: 'warning', cssVar: '--badge-warning-bg', labelKo: '차단됨' },
  DENIED: { tone: 'danger', cssVar: '--badge-error-bg', labelKo: '거부됨' },
  FAILED: { tone: 'danger', cssVar: '--badge-error-bg', labelKo: '실패' },
  COMPENSATING: { tone: 'warning', cssVar: '--badge-warning-bg', labelKo: '보정 중' },
  COMPLETED: { tone: 'success', cssVar: '--badge-success-bg', labelKo: '완료' },
  CANCELLED: { tone: 'neutral', cssVar: '--badge-info-bg', labelKo: '취소됨' },
};

export const MUTATION_RESULT_ELEMENTS_V0 = PRODUCT_MUTATION_RESULT_ELEMENTS_V0;
export const P0_STATE_BOUNDARY_INVARIANTS_V0 = PRODUCT_P0_STATE_BOUNDARY_INVARIANTS_V0;

export function isCanonicalOperationState(value: string): value is CanonicalOperationStateV0 {
  return (CANONICAL_OPERATION_STATES_V0 as readonly string[]).includes(value);
}

export function assertNamespacesSeparatedV0(): boolean {
  return STATE_NAMESPACES_V0.length === 7;
}
